import * as THREE from 'three';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { GridMaterial } from './materials/GridMaterial';

export class SceneManager {
  public readonly scene: THREE.Scene;
  public readonly camera: THREE.PerspectiveCamera;
  public readonly renderer: THREE.WebGLRenderer;
  private composer: EffectComposer;
  private gridMaterial: GridMaterial;
  private clock: THREE.Clock;

  constructor(container: HTMLElement) {
    this.clock = new THREE.Clock();

    // Create scene
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x0a0a0f);
    this.scene.fog = new THREE.FogExp2(0x0a0a0f, 0.005); // Reduced for massive 1800x1800 map

    // Create camera
    this.camera = new THREE.PerspectiveCamera(
      75,
      window.innerWidth / window.innerHeight,
      0.1,
      3000 // Extended for 1800x1800 terrain map
    );
    this.camera.position.set(0, 5, -10);

    // Create renderer
    this.renderer = new THREE.WebGLRenderer({
      antialias: true,
      powerPreference: 'high-performance',
    });
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.0;
    container.appendChild(this.renderer.domElement);

    // Set up post-processing
    this.composer = new EffectComposer(this.renderer);
    this.setupPostProcessing();

    // Create environment
    this.gridMaterial = this.createEnvironment();

    // Handle resize
    window.addEventListener('resize', this.handleResize);
  }

  private setupPostProcessing(): void {
    // Render pass
    const renderPass = new RenderPass(this.scene, this.camera);
    this.composer.addPass(renderPass);

    // Bloom pass for synthwave glow
    const bloomPass = new UnrealBloomPass(
      new THREE.Vector2(window.innerWidth, window.innerHeight),
      0.8,  // strength
      0.4,  // radius
      0.85  // threshold
    );
    this.composer.addPass(bloomPass);
  }

  private createEnvironment(): GridMaterial {
    // Create ground plane with synthwave grid (expanded for 1800x1800 terrain map)
    const groundSize = 2000;
    const groundGeometry = new THREE.PlaneGeometry(groundSize, groundSize);
    const gridMaterial = new GridMaterial();
    const ground = new THREE.Mesh(groundGeometry, gridMaterial);
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = 0;
    this.scene.add(ground);

    // Add ambient light
    const ambientLight = new THREE.AmbientLight(0x1a1a2e, 0.5);
    this.scene.add(ambientLight);

    // Add directional light with purple tint
    const directionalLight = new THREE.DirectionalLight(0x8000ff, 0.5);
    directionalLight.position.set(10, 20, 10);
    this.scene.add(directionalLight);

    // Add point lights for atmosphere
    const pinkLight = new THREE.PointLight(0xff00ff, 1, 100);
    pinkLight.position.set(20, 10, 0);
    this.scene.add(pinkLight);

    const cyanLight = new THREE.PointLight(0x00ffff, 1, 100);
    cyanLight.position.set(-20, 10, 0);
    this.scene.add(cyanLight);

    // Add horizon glow (distant city effect)
    this.createHorizonGlow();

    return gridMaterial;
  }

  private createHorizonGlow(): void {
    // Create glowing horizon line (expanded for 1800x1800 terrain map)
    const horizonGeometry = new THREE.PlaneGeometry(2000, 80);
    const horizonMaterial = new THREE.ShaderMaterial({
      uniforms: {
        color1: { value: new THREE.Color(0xff00ff) },
        color2: { value: new THREE.Color(0x0a0a0f) },
      },
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform vec3 color1;
        uniform vec3 color2;
        varying vec2 vUv;
        void main() {
          float gradient = smoothstep(0.0, 0.5, vUv.y);
          vec3 color = mix(color1, color2, gradient);
          float alpha = 1.0 - gradient;
          gl_FragColor = vec4(color, alpha * 0.5);
        }
      `,
      transparent: true,
      side: THREE.DoubleSide,
      depthWrite: false,
    });

    const horizon = new THREE.Mesh(horizonGeometry, horizonMaterial);
    horizon.position.set(0, 15, 950); // Pushed back for 1800x1800 terrain map
    this.scene.add(horizon);
  }

  private handleResize = (): void => {
    const width = window.innerWidth;
    const height = window.innerHeight;

    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();

    this.renderer.setSize(width, height);
    this.composer.setSize(width, height);
  };

  public render(): void {
    // Update grid shader time
    const time = this.clock.getElapsedTime();
    this.gridMaterial.update(time);

    // Render with post-processing
    this.composer.render();
  }

  public destroy(): void {
    window.removeEventListener('resize', this.handleResize);
    this.renderer.dispose();
  }
}
